import React, { useEffect, useState } from "react";
import { api, fmtUSD, fmtRelative } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Cloud, CheckCircle2, Plug, Sparkles, Activity, ShieldCheck,
  Database, Server, Cpu, AlertCircle, Clock, Zap, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import TrustPolicyPanel from "../components/TrustPolicyPanel";
import ApiTokensPanel from "../components/ApiTokensPanel";

const PROVIDER_LOGOS = {
  gcp:     { letter: "G", label: "GCP" },
  aws:     { letter: "A", label: "AWS" },
  azure:   { letter: "Az", label: "AZ" },
  oci:     { letter: "O", label: "OCI" },
  alibaba: { letter: "Ali", label: "ALI" },
};

export default function Settings() {
  const [config, setConfig] = useState(null);
  const [providers, setProviders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [credentials, setCredentials] = useState({});
  const [connecting, setConnecting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);

  const load = async () => {
    try {
      const [c, p] = await Promise.all([api.config(), api.providers.list()]);
      setConfig(c); setProviders(p);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); const id = setInterval(load, 12000); return () => clearInterval(id); }, []);

  const openWizard = (p) => {
    setSelected(p);
    setCredentials({});
    setValidation(null);
  };

  const validateConnection = async () => {
    if (!selected) return;
    setValidating(true);
    try {
      const res = await api.providers.validate(selected.id, credentials);
      setValidation(res);
      if (res.overall_status === "pass") {
        toast.success("All checks passed", { description: "Ready to connect." });
      } else if (res.overall_status === "incomplete") {
        toast.error("Required fields missing", { description: res.missing_fields.join(", ") });
      } else {
        toast.error("Some checks failed", { description: "Review the validation panel." });
      }
    } catch (e) {
      toast.error("Validation request failed", { description: e?.response?.data?.detail || e.message });
    } finally { setValidating(false); }
  };

  const connect = async () => {
    if (!selected) return;
    setConnecting(true);
    try {
      const res = await api.providers.connect(selected.id, credentials);
      toast.success(`${selected.name} connected`, { description: res.message });
      setSelected(null);
      setValidation(null);
      load();
    } catch (e) {
      toast.error("Connection failed", { description: e?.response?.data?.detail || e.message });
    } finally { setConnecting(false); }
  };

  const disconnect = async (p, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Disconnect ${p.name}? You'll need to re-onboard to resume scans.`)) return;
    try {
      await api.providers.disconnect(p.id);
      toast.success(`${p.name} disconnected`);
      load();
    } catch (err) { toast.error("Disconnect failed"); }
  };

  if (!config) return <div className="px-6 lg:px-10 py-8 text-muted-foreground">Loading settings…</div>;

  return (
    <div className="px-6 lg:px-10 py-8 space-y-6" data-testid="settings-page">
      {/* Provider onboarding section */}
      <div data-testid="provider-onboarding-section">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="cs-heading text-base font-semibold tracking-tight">Cloud Providers</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Connect a provider in &lt;10 minutes · cost data starts flowing within 30 minutes of first sync</p>
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <button
              onClick={() => { load(); toast.success("Refreshed"); }}
              data-testid="settings-refresh"
              className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full border border-border hover:border-[#00579e] hover:text-[#00579e] transition-colors text-[11px] font-medium"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <span className="hidden sm:inline-flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5 text-[#15803d]" />
              Read-only by design · no write permissions ever requested
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {providers.map(p => (
            <ProviderCard key={p.id} provider={p} onConnect={() => openWizard(p)} onDisconnect={(e) => disconnect(p, e)} />
          ))}
        </div>
      </div>

      {/* Progressive Trust (ASTRA AutoFix) */}
      <TrustPolicyPanel />

      {/* API tokens + MCP bridge */}
      <ApiTokensPanel />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="cs-card p-6">
          <h2 className="cs-heading text-base font-semibold tracking-tight mb-1">Agent Configuration</h2>
          <p className="text-xs text-muted-foreground mb-4">Live runtime parameters</p>
          <Row label="Mode" value="Live" ok />
          <Row label="Scan interval" value={`every ${config.scan_interval_hours}h`} ok />
          <Row label="Cost ingestion SLA" value="≤ 30 min" ok />
          <Row label="MongoDB" value="connected" ok />
        </div>

        <div className="cs-card p-6">
          <h2 className="cs-heading text-base font-semibold tracking-tight mb-1 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#00579e]" /> AI Integrations
          </h2>
          <p className="text-xs text-muted-foreground mb-4">Optional LLM keys</p>
          <Row label="Executive Briefing" value={config.ai_enabled ? `${config.ai_model} (Claude)` : "Heuristic engine (rule-based · no LLM key set)"} ok={!!config.ai_enabled} />
          <Row label="Ask Dex chatbot" value={config.dex_enabled ? "enabled" : "disabled"} ok={!!config.dex_enabled} />
          <Row label="Slack alerts" value={config.notifications?.slack_configured ? "configured" : "not configured"} ok={!!config.notifications?.slack_configured} />
          <Row label="Email (SendGrid)" value={config.notifications?.email_configured ? "configured" : "not configured"} ok={!!config.notifications?.email_configured} />
        </div>
      </div>

      <div className="cs-card p-6">
        <h2 className="cs-heading text-base font-semibold tracking-tight mb-1">Services Monitored Across Providers</h2>
        <p className="text-xs text-muted-foreground mb-4">Each cost-optimization scan inspects these service categories on every connected cloud</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <ServiceCard icon={Database} title="Analytics & Warehouses" detail="BigQuery · Redshift · Synapse · Athena · MaxCompute" />
          <ServiceCard icon={Server} title="Managed Databases" detail="Cloud SQL · RDS · Azure SQL · OCI Autonomous · PolarDB" />
          <ServiceCard icon={Cpu} title="Compute & Containers" detail="VMs · GKE/EKS/AKS · OCI Compute · ECS · Functions" />
          <ServiceCard icon={Zap} title="AI / ML / Storage" detail="Vertex · SageMaker · Azure ML · Object Storage · CDN" />
        </div>
      </div>

      {/* Onboarding wizard */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && (setSelected(null), setValidation(null))}>
        <DialogContent className="max-w-4xl rounded-xl max-h-[90vh] overflow-y-auto" data-testid="onboarding-wizard">
          {selected && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-2">
                  <ProviderBadge id={selected.id} size="md" color={selected.color} />
                  <div className="flex-1">
                    <DialogTitle className="cs-heading text-lg tracking-tight">Connect {selected.name}</DialogTitle>
                    <DialogDescription className="text-xs">
                      {selected.auth_method || "Cloud onboarding"} · ingestion starts within 30 min of first sync
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              {/* Steps */}
              <div data-testid="wizard-steps-section">
                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">Onboarding steps</div>
                <div className="space-y-2.5">
                  {selected.connect_steps.map(s => (
                    <div key={s.step} className="flex gap-3" data-testid={`step-${s.step}`}>
                      <div className="w-6 h-6 rounded-full bg-[#00579e] text-white text-xs font-semibold flex items-center justify-center shrink-0">
                        {s.step}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm">{s.title}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{s.detail}</div>
                      </div>
                      <div className="text-[11px] text-muted-foreground whitespace-nowrap shrink-0 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> ~{s.estimated_minutes} min
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Permissions panel */}
              {selected.permissions?.length > 0 && (
                <div className="border-t border-border pt-4" data-testid="wizard-permissions-section">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">Required permissions / IAM roles</div>
                  <div className="border border-border rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-[hsl(var(--cs-tint))]">
                        <tr className="text-left">
                          <th className="px-3 py-2 font-medium text-muted-foreground">Role / Action</th>
                          <th className="px-3 py-2 font-medium text-muted-foreground">Scope</th>
                          <th className="px-3 py-2 font-medium text-muted-foreground">Purpose</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.permissions.map((p, i) => (
                          <tr key={i} className="border-t border-border" data-testid={`perm-row-${i}`}>
                            <td className="px-3 py-2 cs-mono text-[11px] font-semibold text-foreground">{p.role}</td>
                            <td className="px-3 py-2 text-muted-foreground">{p.scope}</td>
                            <td className="px-3 py-2 text-foreground">{p.purpose}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Ingestion modes */}
              {selected.ingestion_modes?.length > 0 && (
                <div className="border-t border-border pt-4" data-testid="wizard-ingestion-section">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">Ingestion modes (configured automatically)</div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {selected.ingestion_modes.map((m, i) => (
                      <div key={i} className="border border-border rounded-lg p-3" data-testid={`mode-row-${i}`}>
                        <div className="flex items-center gap-2">
                          <Zap className="w-3.5 h-3.5 text-[#00579e]" />
                          <div className="text-xs font-semibold text-foreground">{m.mode}</div>
                        </div>
                        <div className="mt-1.5 text-[11px] text-muted-foreground leading-relaxed">{m.description}</div>
                        <div className="mt-2 flex items-center gap-2 text-[10px] cs-mono text-muted-foreground">
                          <span className="cs-pill bg-[#dbeafe] text-[#1e40af]">{m.schedule}</span>
                          <span className="cs-pill bg-[#f3f4f6] text-[#6b7280]">lag {m.lag}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Credentials form */}
              <div className="border-t border-border pt-4" data-testid="wizard-credentials-section">
                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">Paste credentials</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selected.credential_form.map(f => (
                    <div key={f.field} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                      <label className="text-xs font-medium text-foreground mb-1 block">
                        {f.label}{f.required && <span className="text-[#ef4444] ml-1">*</span>}
                      </label>
                      {f.type === "textarea" ? (
                        <Textarea rows={4}
                                  value={credentials[f.field] || ""}
                                  onChange={(e) => setCredentials({...credentials, [f.field]: e.target.value})}
                                  data-testid={`creds-${f.field}`}
                                  placeholder={f.placeholder} className="rounded-lg cs-mono text-[11px]" />
                      ) : (
                        <Input type={f.type === "password" ? "password" : "text"}
                               value={credentials[f.field] || ""}
                               onChange={(e) => setCredentials({...credentials, [f.field]: e.target.value})}
                               data-testid={`creds-${f.field}`}
                               placeholder={f.placeholder} className="rounded-lg cs-mono" />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Validation panel */}
              {validation && (
                <div className="border-t border-border pt-4" data-testid="wizard-validation-results">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Connection test results</div>
                    <span className={`cs-pill ${
                      validation.overall_status === "pass" ? "bg-[#dcfce7] text-[#15803d]"
                      : validation.overall_status === "incomplete" ? "bg-[#fef3c7] text-[#92400e]"
                      : "bg-[#fee2e2] text-[#b91c1c]"
                    }`} data-testid="validation-overall-pill">
                      {validation.overall_status === "pass" ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                      {validation.overall_status}
                    </span>
                  </div>
                  <div className="border border-border rounded-lg overflow-hidden">
                    {validation.checks.map((c, i) => (
                      <div key={c.id} className={`flex items-center gap-3 px-3 py-2 text-xs ${i > 0 ? "border-t border-border" : ""}`}
                           data-testid={`validation-check-${c.id}`}>
                        {c.status === "pass"
                          ? <CheckCircle2 className="w-4 h-4 text-[#15803d] shrink-0" />
                          : c.status === "skipped"
                          ? <Clock className="w-4 h-4 text-[#92400e] shrink-0" />
                          : <AlertCircle className="w-4 h-4 text-[#b91c1c] shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-foreground">{c.label}</div>
                          <div className="text-[10px] text-muted-foreground cs-mono">{c.step}</div>
                        </div>
                        <div className="text-[11px] cs-mono text-muted-foreground text-right shrink-0">
                          {c.message}{c.duration_ms ? ` · ${c.duration_ms}ms` : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between border-t border-border pt-4 -mb-2">
                <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#15803d]" /> Credentials encrypted at rest with KMS · TLS 1.3 in transit · no plaintext in logs
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setSelected(null)} className="rounded-full">Cancel</Button>
                  <Button variant="outline" onClick={validateConnection} disabled={validating} data-testid="test-connection-button"
                          className="rounded-full">
                    {validating ? "Testing…" : <><Zap className="w-4 h-4 mr-1.5" /> Test connection</>}
                  </Button>
                  <Button onClick={connect} disabled={connecting} data-testid="connect-button"
                          className="bg-[#00579e] hover:bg-[#004580] text-white rounded-full">
                    {connecting ? "Connecting…" : <><Plug className="w-4 h-4 mr-1.5" /> Connect</>}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProviderCard({ provider, onConnect, onDisconnect }) {
  const connected = provider.status === "connected";
  const stale = connected && provider.last_sync_at &&
                (Date.now() - new Date(provider.last_sync_at).getTime()) > 30 * 60 * 1000;
  return (
    <div data-testid={`provider-card-${provider.id}`}
         className={`cs-card p-4 flex flex-col h-full ${connected ? "border-[#15803d]/40 bg-[#dcfce7]/20" : ""}`}>
      <div className="flex items-center justify-between mb-3">
        <ProviderBadge id={provider.id} color={provider.color} />
        {connected ? (
          <span className="cs-pill bg-[#dcfce7] text-[#15803d]">
            <CheckCircle2 className="w-3 h-3" /> Connected
          </span>
        ) : (
          <span className="cs-pill bg-[#f3f4f6] text-[#6b7280]">Not connected</span>
        )}
      </div>
      <h3 className="cs-heading text-sm font-semibold tracking-tight">{provider.name}</h3>
      <div className="flex-1 min-h-[44px]">
        {connected ? (
          <div className="mt-3 space-y-1 text-[11px] text-muted-foreground">
            <div>{provider.accounts_discovered} accounts · {fmtUSD(provider.monthly_spend_usd)}/mo</div>
            <div className="flex items-center gap-1">
              <span className={`w-1.5 h-1.5 rounded-full ${stale ? "bg-[#f59e0b]" : "bg-[#22c55e] cs-pulse"}`} />
              Synced {fmtRelative(provider.last_sync_at)}
              {stale && <AlertCircle className="w-3 h-3 text-[#f59e0b] ml-0.5" />}
            </div>
          </div>
        ) : (
          <div className="mt-3 text-[11px] text-muted-foreground">
            {provider.supported_services?.slice(0, 3).join(" · ")}
            {provider.supported_services?.length > 3 && ` +${provider.supported_services.length - 3}`}
          </div>
        )}
      </div>
      <div className="mt-4">
        {connected ? (
          <Button variant="outline" size="sm" className="w-full rounded-full h-8 text-xs"
                  onClick={onDisconnect} data-testid={`disconnect-${provider.id}`}>
            Disconnect
          </Button>
        ) : (
          <Button size="sm" onClick={onConnect} data-testid={`connect-${provider.id}`}
                  className="w-full bg-[#00579e] hover:bg-[#004580] text-white rounded-full h-8 text-xs">
            <Plug className="w-3.5 h-3.5 mr-1" /> Connect
          </Button>
        )}
      </div>
    </div>
  );
}

function ProviderBadge({ id, color, size = "sm" }) {
  const meta = PROVIDER_LOGOS[id] || { letter: "?", label: "?" };
  const px = size === "md" ? "w-10 h-10 text-base" : "w-7 h-7 text-xs";
  return (
    <div className={`${px} rounded-lg flex items-center justify-center font-bold text-white shrink-0`}
         style={{ background: color || "#6b7280" }}>
      {meta.letter}
    </div>
  );
}

function Row({ label, value, ok }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1.5">
        {ok !== undefined && (ok ? <CheckCircle2 className="w-4 h-4 text-[#15803d]" /> : <Activity className="w-4 h-4 text-muted-foreground" />)}
        <span className="text-sm font-medium cs-mono">{value}</span>
      </div>
    </div>
  );
}

function ServiceCard({ icon: Icon, title, detail }) {
  return (
    <div className="border border-border rounded-lg p-4">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#00579e]" />
        <div className="cs-heading text-sm font-semibold">{title}</div>
      </div>
      <div className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{detail}</div>
    </div>
  );
}
